import { useState, useEffect, useRef } from 'react'

/* ─── helpers ───────────────────────────────────────────────────────────── */
function useCountUp(target: number, duration = 2000, start = false) {
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (!start) return
    let t0: number | null = null
    const step = (ts: number) => {
      if (!t0) t0 = ts
      const p = Math.min((ts - t0) / duration, 1)
      setCount(Math.floor(p * target))
      if (p < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [target, duration, start])
  return count
}

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true) }, { threshold })
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return { ref, inView }
}

/* ─── Google Play badge ─────────────────────────────────────────────────── */
function GooglePlayBadge({ size = 'md' }: { size?: 'sm' | 'md' }) {
  return (
    <a
      href="https://play.google.com/store"
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-3 bg-black text-white rounded-2xl hover:bg-gray-900 transition-all duration-200 hover:scale-105 active:scale-100 shadow-lg hover:shadow-xl ${size === 'sm' ? 'px-4 py-2.5' : 'px-5 py-3'}`}
      aria-label="Get Refnix on Google Play"
    >
      <svg width={size === 'sm' ? '22' : '28'} height={size === 'sm' ? '22' : '28'} viewBox="0 0 48 48" fill="none">
        <path d="M7.705 4.043C7.292 4.118 7 4.491 7 4.947V43.053C7 43.509 7.292 43.882 7.705 43.957L8 44L26.708 25.292V23.708L8 5L7.705 4.043Z" fill="url(#gp1)"/>
        <path d="M32.833 31.417L26.708 25.292V23.708L32.833 17.583L33.208 17.792L40.417 21.917C42.458 23.083 42.458 24.917 40.417 26.083L33.208 30.208L32.833 31.417Z" fill="url(#gp2)"/>
        <path d="M33.208 30.208L26.708 23.708L8 42.417C8.708 43.208 9.875 43.292 11.208 42.542L33.208 30.208Z" fill="url(#gp3)"/>
        <path d="M33.208 17.792L11.208 5.458C9.875 4.708 8.708 4.792 8 5.583L26.708 24.292L33.208 17.792Z" fill="url(#gp4)"/>
        <defs>
          <linearGradient id="gp1" x1="20.308" y1="5.833" x2="1.458" y2="24.083" gradientUnits="userSpaceOnUse">
            <stop stopColor="#00A0FF"/><stop offset="1" stopColor="#00E3FF"/>
          </linearGradient>
          <linearGradient id="gp2" x1="42.667" y1="24" x2="6.583" y2="24" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FFE000"/><stop offset="1" stopColor="#FF9C00"/>
          </linearGradient>
          <linearGradient id="gp3" x1="29.458" y1="26.958" x2="3.708" y2="52.708" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FF3A44"/><stop offset="1" stopColor="#C31162"/>
          </linearGradient>
          <linearGradient id="gp4" x1="4.292" y1="-3.583" x2="16.583" y2="8.708" gradientUnits="userSpaceOnUse">
            <stop stopColor="#32A071"/><stop offset="1" stopColor="#00F076"/>
          </linearGradient>
        </defs>
      </svg>
      <div className="text-left">
        <div className={`text-gray-300 font-medium leading-none uppercase tracking-wide ${size === 'sm' ? 'text-[9px]' : 'text-[10px]'}`}>Get it on</div>
        <div className={`font-semibold leading-tight mt-0.5 ${size === 'sm' ? 'text-[15px]' : 'text-[18px]'}`}>Google Play</div>
      </div>
    </a>
  )
}

/* ─── Company logo via Google Favicon Service + letter fallback ─────────── */
function CompanyLogo({ domain, name }: { domain: string; name: string }) {
  const [errored, setErrored] = useState(false)
  const colors = ['bg-violet-500','bg-purple-500','bg-indigo-500','bg-blue-500','bg-fuchsia-500']
  const bg = colors[name.charCodeAt(0) % colors.length]

  if (errored) {
    return (
      <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <span className="text-white text-sm font-bold">{name[0].toUpperCase()}</span>
      </div>
    )
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
      alt={name}
      onError={() => setErrored(true)}
      className="w-9 h-9 rounded-xl object-contain bg-white border border-gray-100 shrink-0 p-1"
    />
  )
}

/* ─── Screen content components (kept outside PhoneMockup for perf) ──────── */
const FeedScreen = () => (
  <div className="h-full bg-gradient-to-b from-violet-50 to-white overflow-hidden">
    {/* Search bar */}
    <div className="px-3 pt-2 pb-2">
      <div className="bg-white rounded-xl border border-violet-100 px-3 py-1.5 flex items-center gap-2 shadow-sm">
        <span className="text-gray-300 text-xs">🔍</span>
        <div className="h-2 w-24 bg-gray-200 rounded-full" />
      </div>
    </div>
    {/* Filter chips */}
    <div className="flex gap-1.5 px-3 pb-2 overflow-hidden">
      {['All', 'Bangalore', 'Remote', 'Senior'].map((f, i) => (
        <span key={f} className={`text-[7px] font-bold rounded-full px-2 py-0.5 shrink-0 ${i === 0 ? 'bg-violet-500 text-white' : 'bg-violet-50 text-violet-500 border border-violet-200'}`}>{f}</span>
      ))}
    </div>
    {/* Job cards */}
    <div className="px-3 space-y-2">
      {[
        { role: 'Sr. Frontend Engineer', co: 'Google India', loc: '📍 Bangalore', match: true, logo: 'google.com' },
        { role: 'Product Manager', co: 'Flipkart', loc: '🌐 Remote', match: false, logo: 'flipkart.com' },
        { role: 'Backend Developer', co: 'Razorpay', loc: '📍 Hyderabad', match: true, logo: 'razorpay.com' },
      ].map((j, i) => (
        <div key={i} className="bg-white rounded-xl p-2 shadow-sm border border-violet-100">
          <div className="flex items-center gap-1.5 mb-1">
            <img src={`https://www.google.com/s2/favicons?domain=${j.logo}&sz=32`} alt={j.co}
              onError={(e) => { (e.target as HTMLImageElement).style.display='none' }}
              className="w-5 h-5 rounded object-contain bg-gray-50 border border-gray-100" />
            <div className="flex-1 min-w-0">
              <div className="text-[8.5px] font-black text-gray-800 truncate leading-tight">{j.role}</div>
              <div className="text-[7px] text-violet-600 font-semibold">{j.co} · {j.loc}</div>
            </div>
            {j.match && <span className="text-[6px] bg-green-100 text-green-700 rounded-full px-1 py-0.5 font-bold shrink-0">✓ Match</span>}
          </div>
          <div className="flex gap-1 mt-1">
            <div className="flex-1 h-5 bg-gradient-to-r from-violet-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-[7px] text-white font-bold">Refer Me ✦</span>
            </div>
            <div className="w-5 h-5 rounded-full border border-gray-200 flex items-center justify-center">
              <span className="text-[8px] text-gray-400">♡</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
)

const ProfileScreen = () => (
  <div className="h-full bg-gradient-to-b from-purple-50 to-white overflow-hidden">
    <div className="px-3 pt-2">
      {/* Avatar + name */}
      <div className="flex flex-col items-center pb-3">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow-lg ring-4 ring-white">
          <span className="text-white text-xl font-black">RS</span>
        </div>
        <div className="mt-2 text-[11px] font-black text-gray-900">Rohan Sharma</div>
        <div className="text-[8px] text-violet-600 font-semibold mt-0.5">Sr. Software Engineer · Infosys</div>
        <div className="mt-0.5 text-[7px] text-gray-400">📍 Bangalore, IN</div>
        {/* Stats row */}
        <div className="flex gap-4 mt-2.5 bg-white rounded-xl px-4 py-1.5 shadow-sm border border-violet-100 w-full justify-center">
          {[['47', 'Referrals', 'text-violet-600'], ['312', 'Connections', 'text-violet-600'], ['₹2,340', 'Earned', 'text-green-600']].map(([v, l, c]) => (
            <div key={l} className="text-center">
              <div className={`text-[10px] font-black ${c}`}>{v}</div>
              <div className="text-[6px] text-gray-400">{l}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Skills */}
      <div className="bg-white rounded-xl p-2 shadow-sm border border-violet-100">
        <div className="text-[7px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Skills</div>
        <div className="flex flex-wrap gap-1">
          {['React', 'Node.js', 'AWS', 'TypeScript', 'Docker', 'K8s'].map(s => (
            <span key={s} className="text-[6.5px] bg-violet-100 text-violet-700 rounded-full px-1.5 py-0.5 font-semibold">{s}</span>
          ))}
        </div>
      </div>
      {/* Toggle */}
      <div className="mt-2 bg-gradient-to-r from-violet-500 to-purple-600 rounded-full h-6 flex items-center justify-between px-3 shadow">
        <span className="text-[7.5px] text-white font-bold">Open to Referrals</span>
        <div className="w-8 h-4 bg-white/30 rounded-full flex items-center justify-end pr-0.5">
          <div className="w-3 h-3 bg-white rounded-full" />
        </div>
      </div>
    </div>
  </div>
)

const ReferralsScreen = () => (
  <div className="h-full bg-gradient-to-b from-fuchsia-50 to-white overflow-hidden">
    <div className="px-3 pt-2">
      {/* Earnings card */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-700 rounded-2xl p-3 mb-3 shadow-lg">
        <div className="text-[7px] text-violet-200 font-semibold uppercase tracking-widest">Total Earned</div>
        <div className="text-[22px] font-black text-white leading-tight">₹8,340</div>
        <div className="flex gap-3 mt-1">
          <div><div className="text-[9px] font-bold text-white">12</div><div className="text-[6px] text-violet-200">Referrals</div></div>
          <div><div className="text-[9px] font-bold text-white">3</div><div className="text-[6px] text-violet-200">Hired</div></div>
          <div><div className="text-[9px] font-bold text-white">92%</div><div className="text-[6px] text-violet-200">Success</div></div>
        </div>
      </div>
      {/* Referral list */}
      <div className="text-[7.5px] font-black text-gray-500 uppercase tracking-widest mb-1.5">Recent Referrals</div>
      <div className="space-y-1.5">
        {[
          { name: 'Priya K.', role: 'PM @ Swiggy', status: 'Hired', sc: 'bg-green-100 text-green-700' },
          { name: 'Amit S.', role: 'Dev @ Zomato', status: 'Interview', sc: 'bg-yellow-100 text-yellow-700' },
          { name: 'Neha R.', role: 'Design @ CRED', status: 'Applied', sc: 'bg-violet-100 text-violet-700' },
          { name: 'Karan T.', role: 'SDE @ PhonePe', status: 'In Review', sc: 'bg-blue-100 text-blue-700' },
        ].map((r, i) => (
          <div key={i} className="bg-white rounded-xl p-1.5 shadow-sm border border-violet-100 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-400 to-purple-600 flex items-center justify-center shrink-0">
              <span className="text-white text-[7px] font-bold">{r.name[0]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[7.5px] font-bold text-gray-900 truncate">{r.name}</div>
              <div className="text-[6.5px] text-gray-400">{r.role}</div>
            </div>
            <span className={`text-[6px] rounded-full px-1.5 py-0.5 font-bold shrink-0 ${r.sc}`}>{r.status}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
)

/* ─── Phone Mockup ──────────────────────────────────────────────────────── */
function PhoneMockup() {
  const [activeTab, setActiveTab] = useState(0)
  const SCREENS = [<FeedScreen />, <ProfileScreen />, <ReferralsScreen />]
  const LABELS  = ['Job Feed', 'My Profile', 'Referrals']
  const TOTAL   = SCREENS.length

  // Auto-slide every 2 seconds
  useEffect(() => {
    const id = setInterval(() => setActiveTab(prev => (prev + 1) % TOTAL), 2000)
    return () => clearInterval(id)
  }, [TOTAL])

  return (
    <div className="relative flex items-center justify-center select-none">
      {/* Background glow */}
      <div className="absolute w-72 h-72 rounded-full bg-violet-400 opacity-25 blur-3xl pointer-events-none" />

      {/* ── Phone shell ────────────────────────────────────────────────── */}
      <div
        className="relative z-10"
        style={{
          width: 260,
          height: 530,
          borderRadius: 46,
          background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 50%, #0f0e17 100%)',
          boxShadow: [
            '0 60px 120px rgba(109,40,217,0.5)',
            '0 30px 60px rgba(0,0,0,0.6)',
            'inset 0 1px 0 rgba(255,255,255,0.15)',
            'inset 0 -1px 0 rgba(0,0,0,0.4)',
          ].join(', '),
          padding: 3,
        }}
      >
        {/* Side volume buttons */}
        <div className="absolute -left-[3px] top-24 w-[3px] h-8 bg-gray-600 rounded-l-sm" />
        <div className="absolute -left-[3px] top-36 w-[3px] h-8 bg-gray-600 rounded-l-sm" />
        {/* Power button */}
        <div className="absolute -right-[3px] top-28 w-[3px] h-10 bg-gray-600 rounded-r-sm" />

        {/* Inner screen bezel */}
        <div
          style={{
            borderRadius: 44,
            overflow: 'hidden',
            height: '100%',
            background: '#fff',
            position: 'relative',
          }}
        >
          {/* Status bar */}
          <div className="flex items-center justify-between px-5 pt-3 pb-1 bg-white z-10 relative">
            <span className="text-[9px] font-black text-gray-800">9:41</span>
            {/* Dynamic Island */}
            <div className="absolute left-1/2 top-3 -translate-x-1/2 w-24 h-6 bg-black rounded-full flex items-center justify-center gap-2 shadow-lg">
              <div className="w-2 h-2 rounded-full bg-gray-800 border border-gray-700" />
              <div className="w-4 h-4 rounded-full bg-gray-800 border border-gray-700" />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[8px] text-gray-600 font-semibold">5G</span>
              <span className="text-[9px] text-gray-600">●●●</span>
              <span className="text-[8px] text-gray-600">🔋</span>
            </div>
          </div>

          {/* App header */}
          <div className="flex items-center justify-between px-4 pb-2 pt-1 bg-white border-b border-gray-100">
            <span className="text-[15px] font-black text-violet-700 tracking-tight">Refnix</span>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-violet-50 border border-violet-100 flex items-center justify-center">
                <span className="text-[10px]">🔔</span>
              </div>
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-400 to-purple-700 shadow" />
            </div>
          </div>

          {/* Sliding screen container */}
          <div className="relative overflow-hidden" style={{ height: 'calc(100% - 115px)' }}>
            <div
              className="flex h-full"
              style={{
                width: `${TOTAL * 100}%`,
                transform: `translateX(-${(activeTab * 100) / TOTAL}%)`,
                transition: 'transform 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {SCREENS.map((screen, i) => (
                <div key={i} style={{ width: `${100 / TOTAL}%`, flexShrink: 0 }} className="h-full overflow-hidden">
                  {screen}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom nav */}
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-around py-2.5 border-t border-gray-100 bg-white/95 backdrop-blur-sm">
            {[
              { icon: '🏠', label: 'Feed',    idx: 0 },
              { icon: '🔗', label: 'Network', idx: -1 },
              { icon: '＋', label: 'Post',    idx: -1 },
              { icon: '💬', label: 'Chats',   idx: -1 },
              { icon: '👤', label: 'Profile', idx: 1 },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-0.5">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${activeTab === item.idx ? 'bg-violet-100' : ''}`}>
                  <span className="text-sm">{item.icon}</span>
                </div>
                <span className={`text-[5.5px] font-semibold ${activeTab === item.idx ? 'text-violet-600' : 'text-gray-400'}`}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Dot indicators below phone */}
      <div className="absolute -bottom-8 flex gap-2 items-center">
        {LABELS.map((_, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            className={`rounded-full transition-all duration-300 ${
              activeTab === i ? 'w-5 h-2 bg-violet-600' : 'w-2 h-2 bg-violet-200'
            }`}
            aria-label={LABELS[i]}
          />
        ))}
      </div>

      {/* Label below dots */}
      <div className="absolute -bottom-14 text-xs font-semibold text-violet-500 tracking-wide">
        {LABELS[activeTab]}
      </div>

      {/* Floating badges */}
      <div
        className="absolute -left-10 top-20 bg-white rounded-2xl shadow-xl px-3 py-2 border border-violet-100 z-20"
        style={{ animation: 'floatA 3s ease-in-out infinite' }}
      >
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-sm">✓</div>
          <div>
            <div className="text-[9px] font-black text-gray-800">Referred!</div>
            <div className="text-[8px] text-green-600 font-semibold">Google India</div>
          </div>
        </div>
      </div>

      <div
        className="absolute -right-10 bottom-28 bg-white rounded-2xl shadow-xl px-3 py-2 border border-violet-100 z-20"
        style={{ animation: 'floatB 3.5s ease-in-out infinite' }}
      >
        <div className="flex items-center gap-1.5">
          <span className="text-base">💰</span>
          <div>
            <div className="text-[9px] font-black text-gray-800">₹500 earned</div>
            <div className="text-[8px] text-violet-600 font-semibold">Referral bonus</div>
          </div>
        </div>
      </div>

      <div
        className="absolute -right-6 top-16 bg-gradient-to-r from-violet-600 to-purple-700 rounded-2xl shadow-xl px-3 py-1.5 z-20"
        style={{ animation: 'floatC 4s ease-in-out infinite' }}
      >
        <div className="text-[9px] text-white font-bold">12 new matches 🎯</div>
      </div>
    </div>
  )
}

/* ─── Stat card ─────────────────────────────────────────────────────────── */
function StatCard({ value, suffix, label, icon, started }: {
  value: number; suffix: string; label: string; icon: string; started: boolean
}) {
  const count = useCountUp(value, 1800, started)
  return (
    <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-card border border-violet-100 hover:shadow-card-lg transition-all duration-300 hover:-translate-y-1">
      <span className="text-3xl mb-2">{icon}</span>
      <div className="text-3xl font-black text-violet-700">{count.toLocaleString()}{suffix}</div>
      <div className="text-sm text-gray-500 mt-1 text-center font-medium">{label}</div>
    </div>
  )
}

/* ─── Live user counter card ─────────────────────────────────────────────── */
// Starts at a realistic base and ticks up by 1 every 4 seconds so visitors
// literally watch the community grow while they read the page.
const LIVE_BASE = 10_247

function LiveUserCard({ started }: { started: boolean }) {
  const rolledIn  = useCountUp(LIVE_BASE, 1800, started)   // initial count-up animation
  const [live, setLive] = useState(0)                       // extra ticks after animation

  useEffect(() => {
    if (!started) return
    // start ticking 2 s after the count-up would finish
    const delay = setTimeout(() => {
      const id = setInterval(() => setLive(p => p + 1), 4000)
      return () => clearInterval(id)
    }, 2000)
    return () => clearTimeout(delay)
  }, [started])

  const displayed = rolledIn + live

  return (
    <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-card border border-violet-100 hover:shadow-card-lg transition-all duration-300 hover:-translate-y-1 relative overflow-hidden">
      {/* subtle green shimmer strip at top */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-green-400 to-transparent opacity-70" />

      <span className="text-3xl mb-2">👥</span>

      <div className="flex items-end gap-1.5">
        <div className="text-3xl font-black text-violet-700 tabular-nums">
          {displayed.toLocaleString()}+
        </div>
      </div>

      {/* LIVE pill */}
      <div className="flex items-center gap-1.5 mt-1.5 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse shrink-0" />
        <span className="text-[10px] font-bold text-green-700 uppercase tracking-wide">Live · Growing</span>
      </div>

      <div className="text-sm text-gray-500 mt-1.5 text-center font-medium">Members &amp; counting</div>
    </div>
  )
}

/* ─── Feature card ──────────────────────────────────────────────────────── */
function FeatureCard({ icon, title, desc, gradient }: { icon: string; title: string; desc: string; gradient: string }) {
  return (
    <div className="group p-6 bg-white rounded-2xl border border-violet-100 shadow-card hover:shadow-card-lg transition-all duration-300 hover:-translate-y-1">
      <div className={`w-12 h-12 rounded-2xl ${gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
        <span className="text-2xl">{icon}</span>
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
    </div>
  )
}

/* ─── Step card ─────────────────────────────────────────────────────────── */
function StepCard({ step, title, desc, color }: { step: number; title: string; desc: string; color: string }) {
  return (
    <div className="flex gap-4 items-start">
      <div className={`w-10 h-10 rounded-2xl ${color} flex items-center justify-center font-black text-white text-lg shrink-0 shadow-lg`}>{step}</div>
      <div>
        <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
        <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
      </div>
    </div>
  )
}

/* ─── Testimonial card ──────────────────────────────────────────────────── */
function TestimonialCard({ name, role, company, text, avatarGradient }: {
  name: string; role: string; company: string; text: string; avatarGradient: string
}) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-card border border-violet-100 hover:shadow-card-lg transition-all duration-300 hover:-translate-y-1 flex flex-col gap-4">
      <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <span key={i} className="text-yellow-400 text-sm">★</span>)}</div>
      <p className="text-gray-600 text-sm leading-relaxed italic flex-1">"{text}"</p>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full ${avatarGradient} flex items-center justify-center font-bold text-white text-sm shrink-0`}>
          {name[0]}
        </div>
        <div>
          <div className="font-semibold text-gray-900 text-sm">{name}</div>
          <div className="text-xs text-violet-600">{role} · {company}</div>
        </div>
      </div>
    </div>
  )
}

/* ─── Companies data ─────────────────────────────────────────────────────── */
const COMPANIES = [
  { name: 'Google India',  domain: 'google.com'     },
  { name: 'Microsoft',     domain: 'microsoft.com'  },
  { name: 'Amazon',        domain: 'amazon.in'      },
  { name: 'Flipkart',      domain: 'flipkart.com'   },
  { name: 'Infosys',       domain: 'infosys.com'    },
  { name: 'TCS',           domain: 'tcs.com'        },
  { name: 'Wipro',         domain: 'wipro.com'      },
  { name: 'Swiggy',        domain: 'swiggy.com'     },
  { name: 'Zomato',        domain: 'zomato.com'     },
  { name: 'Razorpay',      domain: 'razorpay.com'   },
  { name: 'CRED',          domain: 'cred.club'      },
  { name: 'PhonePe',       domain: 'phonepe.com'    },
  { name: 'Paytm',         domain: 'paytm.com'      },
  { name: 'Meesho',        domain: 'meesho.com'     },
  { name: 'Atlassian',     domain: 'atlassian.com'  },
  { name: 'Zepto',         domain: 'zeptonow.com'   },
]

/* ─── Main Landing Page ─────────────────────────────────────────────────── */
export default function LandingPage() {
  const statsRef = useInView(0.15)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Duplicate for seamless infinite marquee
  const marqueeItems = [...COMPANIES, ...COMPANIES]

  return (
    <div className="min-h-screen bg-[#FDFAFF] overflow-x-hidden">

      {/* ── Navbar ──────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-violet-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/icon-192.png" alt="Refnix" className="w-8 h-8 rounded-xl object-cover shadow-violet" />
            <span className="text-xl font-black text-gray-900">Refnix</span>
          </div>

          <div className="hidden md:flex items-center gap-6">
            {[['#how-it-works','How it works'],['#features','Features'],['#testimonials','Success stories']].map(([href, label]) => (
              <a key={href} href={href} className="text-sm font-medium text-gray-600 hover:text-violet-600 transition-colors no-underline">{label}</a>
            ))}
          </div>

          <div className="hidden md:block">
            <a href="https://play.google.com/store" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-purple-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-violet hover:shadow-lg transition-all duration-200 hover:scale-105 no-underline">
              Download App
            </a>
          </div>

          <button className="md:hidden p-2 rounded-lg hover:bg-violet-50 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
            <div className="w-5 h-0.5 bg-gray-700 mb-1 transition-all" />
            <div className="w-5 h-0.5 bg-gray-700 mb-1" />
            <div className="w-4 h-0.5 bg-gray-700" />
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-violet-100 bg-white px-4 py-4 flex flex-col gap-4">
            {[['#how-it-works','How it works'],['#features','Features'],['#testimonials','Success stories']].map(([href, label]) => (
              <a key={href} href={href} onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 no-underline">{label}</a>
            ))}
            <GooglePlayBadge />
          </div>
        )}
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-16 pb-32">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-200 rounded-full opacity-30 blur-3xl -translate-y-1/2 pointer-events-none" />
        <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-200 rounded-full opacity-20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-fuchsia-200 rounded-full opacity-20 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 rounded-full px-4 py-1.5 text-sm font-semibold mb-6">
                <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
                India's #1 Referral Network
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.1] mb-6">
                Get Referred.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-700">Get Hired.</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-500 leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
                Refnix connects ambitious job seekers with insiders at top companies.
                Skip the cold applications — get a warm referral and land your dream job faster.
              </p>
              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4 mb-8">
                <GooglePlayBadge />
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span className="flex -space-x-1.5">
                    {['bg-violet-400','bg-purple-500','bg-fuchsia-500','bg-violet-600'].map((c, i) => (
                      <div key={i} className={`w-7 h-7 rounded-full ${c} border-2 border-white`} />
                    ))}
                  </span>
                  <span><strong className="text-gray-900">10,000+</strong> members and growing</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center lg:justify-start text-sm text-gray-500">
                {['Free to download','Verified referrers','Top Indian companies'].map(t => (
                  <span key={t} className="flex items-center gap-1.5"><span className="text-green-500">✓</span>{t}</span>
                ))}
              </div>
            </div>

            {/* Phone */}
            <div className="flex-1 flex justify-center lg:justify-end pt-8 pb-16">
              <PhoneMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ───────────────────────────────────────────────────────── */}
      <section ref={statsRef.ref} className="py-16 bg-gradient-to-b from-white to-violet-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <LiveUserCard started={statsRef.inView} />
            <StatCard value={10000} suffix="+" label="Successful Referrals"   icon="🤝" started={statsRef.inView} />
            <StatCard value={1000}  suffix="+" label="Partner Companies"      icon="🏢" started={statsRef.inView} />
            <StatCard value={92}    suffix="%" label="Interview Success Rate"  icon="🎯" started={statsRef.inView} />
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <div className="inline-block bg-violet-100 text-violet-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">How It Works</div>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">From profile to offer letter</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Refnix makes the referral journey seamless — whether you're a job seeker looking for an in, or a professional ready to pay it forward.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            {[
              { audience: 'For Job Seekers', color: 'bg-violet-100 text-violet-700', steps: [
                { n:1, t:'Create your profile', d:'Add your skills, experience, and target roles. Upload your resume once.', c:'bg-violet-500' },
                { n:2, t:'Browse & apply for referrals', d:'Find insiders at top companies and request a referral in seconds.', c:'bg-purple-600' },
                { n:3, t:'Get interviewed & hired', d:'Your referral surfaces your profile directly to hiring managers — 5× higher callback rate.', c:'bg-fuchsia-600' },
              ]},
              { audience: 'For Referrers (Employees)', color: 'bg-purple-100 text-purple-700', steps: [
                { n:1, t:'List open roles at your company', d:'Post verified job openings. Set preferences for the candidates you\'ll refer.', c:'bg-purple-500' },
                { n:2, t:'Review applicants', d:'Browse profiles, resumes and skills. Accept candidates you believe in.', c:'bg-violet-600' },
                { n:3, t:'Refer & earn', d:'Submit the referral and earn a real bonus when your candidate gets hired.', c:'bg-indigo-600' },
              ]},
            ].map(({ audience, color, steps }) => (
              <div key={audience} className="bg-white rounded-3xl p-8 shadow-card border border-violet-100">
                <div className={`inline-flex items-center gap-2 ${color} rounded-full px-3 py-1 text-xs font-bold mb-6`}>{audience}</div>
                <div className="space-y-6">
                  {steps.map(s => <StepCard key={s.n} step={s.n} title={s.t} desc={s.d} color={s.c} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <section id="features" className="py-20 bg-gradient-to-b from-violet-50/40 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <div className="inline-block bg-violet-100 text-violet-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Features</div>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">Everything you need to succeed</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Built specifically for the Indian job market, Refnix brings professional networking and referrals into one powerful app.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon:'🎯', title:'AI-Powered Job Matching',      desc:'Smart algorithms match your skills to the most relevant openings so you spend time where it counts.',              gradient:'bg-gradient-to-br from-violet-100 to-purple-100' },
              { icon:'🤝', title:'Verified Referrer Network',    desc:'All referrers are verified employees. Every referral is authentic and carries real weight with hiring managers.',  gradient:'bg-gradient-to-br from-purple-100 to-fuchsia-100' },
              { icon:'💬', title:'In-App Messaging',             desc:'Chat with referrers before applying. Build rapport and stand out before your resume even lands in the inbox.',    gradient:'bg-gradient-to-br from-fuchsia-100 to-pink-100'   },
              { icon:'💰', title:'Referral Bonus Wallet',        desc:'Referrers earn real money when their candidates get hired. Track earnings and withdraw instantly.',                gradient:'bg-gradient-to-br from-green-100 to-emerald-100'  },
              { icon:'📄', title:'One-Click Resume Apply',       desc:'Upload your resume once. Apply to multiple referral requests with a single tap — no repetitive form filling.',    gradient:'bg-gradient-to-br from-blue-100 to-violet-100'    },
              { icon:'🏆', title:'Real-Time Status Tracking',    desc:'Get push notifications at every stage — referral accepted, interview scheduled, offer made. Always in the loop.',gradient:'bg-gradient-to-br from-orange-100 to-yellow-100'  },
            ].map(f => <FeatureCard key={f.title} {...f} />)}
          </div>
        </div>
      </section>

      {/* ── Company marquee with Clearbit logos ─────────────────────────── */}
      <section className="py-14 overflow-hidden bg-white border-y border-violet-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-8">
          <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest">Opportunities at India's top companies</p>
        </div>
        <div className="relative overflow-hidden">
          {/* Left/right fade masks */}
          <div className="absolute left-0 top-0 bottom-0 w-20 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, #fff, transparent)' }} />
          <div className="absolute right-0 top-0 bottom-0 w-20 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, #fff, transparent)' }} />

          <div className="flex gap-4 w-max" style={{ animation: 'marquee 30s linear infinite' }}>
            {marqueeItems.map((c, i) => (
              <div key={i} className="inline-flex items-center gap-2.5 bg-white border border-violet-100 rounded-2xl px-4 py-2.5 shadow-sm hover:shadow-md hover:border-violet-300 transition-all duration-200 shrink-0 group">
                <CompanyLogo domain={c.domain} name={c.name} />
                <span className="text-sm font-semibold text-gray-700 group-hover:text-violet-700 transition-colors whitespace-nowrap">{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ────────────────────────────────────────────────── */}
      <section id="testimonials" className="py-20 bg-gradient-to-b from-white to-violet-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <div className="inline-block bg-violet-100 text-violet-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Success Stories</div>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">Real people. Real results.</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Thousands of professionals have transformed their careers through Refnix. Here's what they say.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { name:'Aisha Mehta',  role:'Software Engineer', company:'Google India', avatarGradient:'bg-gradient-to-br from-violet-500 to-purple-600',  text:'I applied to Google 3 times through the portal and heard nothing. On Refnix, I found an insider who referred me and I got a call within a week. Got the offer last month. Refnix literally changed my life.' },
              { name:'Rajan Verma',  role:'Product Manager',   company:'Flipkart',    avatarGradient:'bg-gradient-to-br from-purple-500 to-fuchsia-600',  text:'As someone wanting to break into product, the warm introduction made all the difference. The Refnix chat feature helped me have a real conversation with the referrer before even applying.' },
              { name:'Sneha Kapoor', role:'Data Scientist',    company:'Razorpay',    avatarGradient:'bg-gradient-to-br from-fuchsia-500 to-pink-600',    text:"I've referred 6 people through Refnix and 3 have been hired. I've earned ₹8,000 in bonuses and more importantly, helped people I believe in get their foot in the door. Genuinely rewarding." },
              { name:'Kabir Singh',  role:'Backend Developer', company:'Swiggy',      avatarGradient:'bg-gradient-to-br from-indigo-500 to-violet-600',   text:'The skill-match feature is brilliant. I saw jobs where my exact stack was needed and connected with referrers the same day. Interviewed within 2 weeks of signing up.' },
              { name:'Priya Nair',   role:'UX Designer',       company:'CRED',        avatarGradient:'bg-gradient-to-br from-violet-400 to-purple-700',   text:"Coming from a tier-2 college, referrals were my only real shot at these companies. Refnix gave me access to insiders I could never have reached on LinkedIn. Now I'm at CRED doing work I love." },
              { name:'Dev Anand',    role:'Senior Engineer',   company:'Zepto',       avatarGradient:'bg-gradient-to-br from-purple-400 to-indigo-600',   text:'Referred 12 people in 4 months. The wallet feature is seamless — I get notified the moment someone I referred joins and the bonus hits my account. Best passive income I\'ve found.' },
            ].map(t => <TestimonialCard key={t.name} {...t} />)}
          </div>
        </div>
      </section>

      {/* ── CTA banner ──────────────────────────────────────────────────── */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-violet-600 via-purple-700 to-fuchsia-800" />
            <div className="absolute top-0 right-0 w-80 h-80 bg-white opacity-5 rounded-full -translate-y-1/2 translate-x-1/3 blur-2xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full translate-y-1/2 -translate-x-1/3 blur-2xl" />
            <div className="relative z-10 text-center py-16 px-6 sm:px-12">
              <div className="w-16 h-16 rounded-2xl overflow-hidden mx-auto mb-6 shadow-xl">
                <img src="/icon-192.png" alt="Refnix" className="w-full h-full object-cover" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">Your dream job is one<br />referral away.</h2>
              <p className="text-violet-200 text-lg mb-10 max-w-xl mx-auto">Thousands of professionals join Refnix every week. Download the app today and start building connections that convert to offers.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <GooglePlayBadge />
                <div className="text-violet-200 text-sm">Free download · No credit card required</div>
              </div>
              <div className="flex flex-wrap justify-center gap-8 mt-10 pt-10 border-t border-white/20">
                {[['& growing','Members'],['10K+','Referrals'],['4.8 ★','App Rating'],['92%','Success Rate']].map(([v,l]) => (
                  <div key={l} className="text-center">
                    <div className="text-2xl font-black text-white">{v}</div>
                    <div className="text-violet-300 text-sm">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/icon-192.png" alt="Refnix" className="w-8 h-8 rounded-xl object-cover" />
                <span className="text-xl font-black text-white">Refnix</span>
              </div>
              <p className="text-sm leading-relaxed text-gray-500 max-w-xs mb-5">India's fastest-growing professional referral network. Connecting talent with opportunity — one referral at a time.</p>
              <GooglePlayBadge size="sm" />
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">Company</h4>
              <ul className="space-y-2 text-sm">
                {[['#how-it-works','How it works'],['#features','Features'],['#testimonials','Success stories']].map(([h,l]) => (
                  <li key={l}><a href={h} className="hover:text-violet-400 transition-colors no-underline">{l}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/privacy-policy" className="hover:text-violet-400 transition-colors no-underline">Privacy Policy</a></li>
                <li><span className="text-gray-600 cursor-not-allowed">Terms of Service</span></li>
                <li><span className="text-gray-600 cursor-not-allowed">Cookie Policy</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <p>© {new Date().getFullYear()} Refnix. All rights reserved.</p>
            <p className="text-gray-600">Made with ♥ in India</p>
          </div>
        </div>
      </footer>

      {/* ── Global keyframe animations ───────────────────────────────────── */}
      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes floatA {
          0%, 100% { transform: translateY(0px) rotate(-1deg); }
          50%       { transform: translateY(-12px) rotate(1deg); }
        }
        @keyframes floatB {
          0%, 100% { transform: translateY(0px) rotate(1deg); }
          50%       { transform: translateY(-14px) rotate(-1deg); }
        }
        @keyframes floatC {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  )
}
